import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.recommendationsPerformed === 'true') {
      this.prioritizeSameVendorProducts();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const productContext = {
      vendor: this.dataset.productVendor?.trim(),
      handle: this.dataset.productHandle?.trim(),
      maxProducts: this.dataset.maxProducts,
      currencyCode: this.dataset.currencyCode,
    };
    const vendorProductsPromise =
      intent === 'related'
        ? this.#fetchSameVendorProducts(productContext.vendor, productContext.handle)
        : Promise.resolve([]);

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then(async (result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });

          const hydratedRecommendations = document.getElementById(`product-recommendations-${sectionId}`);
          if (hydratedRecommendations) {
            hydratedRecommendations.productVendor = productContext.vendor;
            hydratedRecommendations.productHandle = productContext.handle;
            hydratedRecommendations.maxProducts = productContext.maxProducts;
            hydratedRecommendations.currencyCode = productContext.currencyCode;
          }
          const vendorProducts = await vendorProductsPromise;
          hydratedRecommendations?.prioritizeSameVendorProducts(vendorProducts);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Put available products from the current product's vendor first. Shopify's
   * native recommendations remain as fallbacks when the vendor has fewer than
   * the requested number of products.
   */
  async prioritizeSameVendorProducts(preloadedProducts = null) {
    if (this.vendorPriorityApplied || this.dataset.intent !== 'related') return;

    const vendor = this.productVendor || this.dataset.productVendor?.trim();
    const currentHandle = this.productHandle || this.dataset.productHandle?.trim();
    const maxProducts = Number.parseInt(this.maxProducts || this.dataset.maxProducts || '4', 10);
    const list = this.querySelector('.resource-list--grid');

    if (!vendor || !currentHandle || !list || !Number.isFinite(maxProducts) || maxProducts < 1) return;

    this.vendorPriorityApplied = true;

    try {
      const sameVendorProducts =
        preloadedProducts || (await this.#fetchSameVendorProducts(vendor, currentHandle));

      if (sameVendorProducts.length === 0) return;

      const selectedHandles = new Set(sameVendorProducts.slice(0, maxProducts).map((candidate) => candidate.handle));
      const fallbackItems = [...list.querySelectorAll(':scope > .resource-list__item')].filter((item) => {
        const link = item.querySelector('a[href*="/products/"]');
        if (!link) return false;

        const handle = new URL(link.href, window.location.origin).pathname.split('/products/')[1]?.split('/')[0];
        return handle && handle !== currentHandle && !selectedHandles.has(handle);
      });

      const vendorItems = sameVendorProducts
        .slice(0, maxProducts)
        .map((candidate) => this.#createVendorRecommendation(candidate));
      const fallbackCount = Math.max(0, maxProducts - vendorItems.length);

      list.replaceChildren(...vendorItems, ...fallbackItems.slice(0, fallbackCount));
    } catch (error) {
      console.warn('Same-vendor recommendations unavailable:', error);
    }
  }

  /**
   * @param {string | undefined} vendor
   * @param {string | undefined} currentHandle
   * @returns {Promise<Array<object>>}
   */
  async #fetchSameVendorProducts(vendor, currentHandle) {
    if (!vendor || !currentHandle) return [];

    try {
      const params = new URLSearchParams({ q: vendor });
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');
      params.set('resources[options][fields]', 'vendor');
      params.set('resources[options][unavailable_products]', 'hide');

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}search/suggest.json?${params.toString()}`);
      if (!response.ok) return [];

      const payload = await response.json();
      const products = payload?.resources?.results?.products || [];

      return products.filter(
        (candidate) =>
          candidate.available &&
          candidate.handle !== currentHandle &&
          candidate.vendor?.localeCompare(vendor, undefined, { sensitivity: 'accent' }) === 0
      );
    } catch (error) {
      console.warn('Same-vendor product lookup unavailable:', error);
      return [];
    }
  }

  /**
   * @param {{ handle: string, title: string, vendor: string, price: string, image?: string, featured_image?: { url?: string, alt?: string } }} product
   * @returns {HTMLDivElement}
   */
  #createVendorRecommendation(product) {
    const item = document.createElement('div');
    item.className = 'resource-list__item';

    const card = document.createElement('article');
    card.className = 'vendor-recommendation-card';

    const link = document.createElement('a');
    link.className = 'vendor-recommendation-card__link';
    link.href = `/products/${encodeURIComponent(product.handle)}`;

    const media = document.createElement('div');
    media.className = 'vendor-recommendation-card__media';

    const imageUrl = product.featured_image?.url || product.image;
    if (imageUrl) {
      const image = document.createElement('img');
      const separator = imageUrl.includes('?') ? '&' : '?';
      image.src = `${imageUrl}${separator}width=900`;
      image.alt = product.featured_image?.alt || product.title;
      image.loading = 'lazy';
      image.width = 900;
      image.height = 900;
      media.append(image);
    }

    const content = document.createElement('div');
    content.className = 'vendor-recommendation-card__content';

    const vendor = document.createElement('p');
    vendor.className = 'vendor-recommendation-card__vendor';
    vendor.textContent = product.vendor;

    const title = document.createElement('h3');
    title.className = 'vendor-recommendation-card__title';
    title.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'vendor-recommendation-card__price';
    price.textContent = this.#formatPrice(product.price);

    content.append(vendor, title, price);
    link.append(media, content);
    card.append(link);
    item.append(card);

    return item;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #formatPrice(value) {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return value;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currencyCode || this.dataset.currencyCode || 'USD',
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
